import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";

import { ASK_VYRILL_REGENRATE_RESPONSE } from "../../../Queries/ask-vyrill";
import { updateAskVyrillState } from "../Actions";

import AskVyrillDuration from "./AskVyrillDuration";
import Tooltip from "../../../Shared/Tooltip/Tooltip";
import CustomDateAskVyrill from "./CustomDateAskVyrill";

const customSelectedDateStyles = {
  control: (provided) => ({
    ...provided,
    maxWidth: "236px",
    paddingRight: "25px",
    minHeight: "22px",
    backgroundColor: "#f5f7f9",
  }),
  menu: (provided) => ({
    ...provided,
    width: "236px",
  }),

  dropdownIndicator: (provided) => ({
    ...provided,
    position: "absolute",
    right: "2px",
    top: "3px",
  }),
};

const ChatBoxHeader = ({ isHistoryOpen, handlePrint }) => {
  const [copied, setCopied] = useState(false);

  const messages = useSelector((state) => state.askVyrill.messages);
  const videosCount = useSelector(
    (state) => state.navbarStatsReducer.counts?.videos
  );

  const conversationId = useSelector((state) => state.askVyrill.conversationId);
  const currentBrandId = useSelector(
    (state) => state.navbarStatsReducer.currentBrand._id
  );
  const allConversations = useSelector(
    (state) => state.askVyrill.allConversations
  );
  const messagesLoading = useSelector(
    (state) => state.askVyrill.messagesLoading
  );
  const askVyrillDuration = useSelector(
    (state) => state.askVyrill.askVyrillDuration
  );
  const askVyrillError = useSelector((state) => state.askVyrill.askVyrillError);
  const askVyrillTimoutError = useSelector(
    (state) => state.askVyrill.askVyrillTimoutError
  );

  const dispatch = useDispatch();

  useEffect(() => {
    setCopied(false);
  }, [messages]);

  const handleCopyClick = () => {
    if (messages.length) {
      const allMessages = messages
        ?.map((message) =>
          message.role == "assistant"
            ? `A: ${message.content}\n`
            : `Q: ${message.content}`
        )
        .join("\n");
      navigator?.clipboard
        .writeText(allMessages)
        .then(() => {
          setCopied(true);
        })
        .catch((error) => {
          console.error("Failed to copy text: ", error);
        });
    }
  };

  function getLastUserAndAssistant(array) {
    let lastUser = null;
    let lastAssistant = null;

    for (let i = array.length - 1; i >= 0; i--) {
      if (array[i].role === "user" && lastUser === null) {
        lastUser = array[i];
      } else if (array[i].role === "assistant" && lastAssistant === null) {
        lastAssistant = array[i];
      }

      // If both lastUser and lastAssistant are found,break out of the loop
      if (lastUser !== null && lastAssistant !== null) {
        break;
      }
    }

    return { lastUser, lastAssistant };
  }

  const { refetch: refetchAskVyrill } = useQuery(
    ASK_VYRILL_REGENRATE_RESPONSE,
    {
      skip:
        true ||
        !currentBrandId ||
        !getLastUserAndAssistant(messages)?.lastUser?._id ||
        getLastUserAndAssistant(messages)?.lastAssistant?._id,
      onError: (error) => {
        // Handle the error here
        dispatch(
          updateAskVyrillState({
            askVyrillTimoutError: true,
            messagesLoading: false,
          })
        );
      },
    }
  );

  const handleRegenerate = () => {
    if (messages) {
      const lastResponse = getLastUserAndAssistant(messages);
      const newArray = messages.filter(
        (obj) => obj._id !== lastResponse?.lastAssistant?._id
      );
      dispatch(
        updateAskVyrillState({
          messagesLoading: true,
          messages: newArray,
        })
      );

      refetchAskVyrill({
        conversationId: conversationId,
        questionId: lastResponse?.lastUser?._id,
        responseId: lastResponse?.lastAssistant?._id,
      }).then((result) => {
        const newArray = messages.filter(
          (obj) => obj._id !== lastResponse?.lastAssistant?._id
        );

        if (!result?.data?.regenerate) {
          dispatch(
            updateAskVyrillState({
              askVyrillError: true,
              messagesLoading: false,
              isFirstPrompt: false,
            })
          );
        } else {
          dispatch(
            updateAskVyrillState({
              messages: [...newArray, result?.data?.regenerate],
              messagesLoading: false,
              isFirstPrompt: false,
            })
          );
        }
      });
    }
  };

  const mapDataToString = (messages) => {
    return messages
      .map((message) =>
        message.role == "assistant"
          ? `A: ${message.content}\n`
          : `Q: ${message.content}`
      )
      .join("\n");
  };

  const emailHandler = () => {
    const activeConversation = allConversations?.find(
      (item) => item?._id === conversationId
    );

    const chatInfo = `Chat title: ${activeConversation?.title}`;

    if (messages.length) {
      const body = `${chatInfo}\n\n${mapDataToString(messages)}\n\n`;
      const link = `mailto:jhon@example.com?subject=${encodeURIComponent(
        "Ask vyrill chat"
      )}&body=${encodeURIComponent(body)}`;

      window.location.href = link;
    }
  };

  return (
    <div
      className={`select-date-section ${
        isHistoryOpen ? "non-expanded-header" : "expanded-header"
      }`}
    >
      <div className="d-flex align-items-center gap-1">
        <div className="label">Based on the videos captured in</div>
        <div className="date-range">
          <AskVyrillDuration customStyles={customSelectedDateStyles} />
        </div>
        {askVyrillDuration?.value === "custom" && <CustomDateAskVyrill />}
      </div>

      <div className="videos-content">
        Videos - <b>{videosCount}</b>
      </div>
      <div className="d-flex align-items-center">
        <img
          src={require("@src/assets/images/icons/copy-icon.png").default}
          alt=""
          width="13"
          // height="13"
        />
        <span
          className="copy-name"
          onClick={messagesLoading ? () => {} : handleCopyClick}
        >
          {copied ? "Copied" : "Copy"}
        </span>
        <Tooltip text={"Email"} id="email-icon-askvyrill">
          <img
            role="button"
            src={require("@src/assets/images/icons/email-icon.png").default}
            alt=""
            width="20"
            onClick={messagesLoading ? () => {} : emailHandler}
          />
        </Tooltip>
        <span style={{ margin: "0 25px 0 25px" }} role="button">
          <Tooltip text={"Downlaod"} id="dowload-icon-askvyrill">
            <img
              id="dowload-icon-askvyrill"
              src={
                require("@src/assets/images/icons/download-icon.png").default
              }
              alt=""
              width="20"
              onClick={handlePrint}
            />
          </Tooltip>
        </span>

        <Tooltip text={"Regenerate"}>
          <span
            onClick={
              askVyrillError || askVyrillTimoutError || messagesLoading ? () => {} : handleRegenerate
            }
            role="button"
          >
            <img
              src={
                require("@src/assets/images/vyrill/icons/repeat-icon.png")
                  .default
              }
              alt=""
              width="18"
            />
          </span>
        </Tooltip>
      </div>
    </div>
  );
};
export default ChatBoxHeader;
